/* eslint-disable react-hooks/exhaustive-deps */
import DeleteModal from "components/DeleteModal";
import DirectorNotesButton from "components/DirectorNotesButton";
import DocumentViewerModal from "components/DocumentViewer";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  deleteWitnessToWitnessProction,
  editWitnessProction2 as editWitnessProtaction2,
  fetchWitnessProctionById,
} from "services/witnessProtectionService";
import {
  FEEDBACK_MODEL,
  FILE_URL,
  Roles,
  WitnessForWitnessProtection,
  WitnessProtection,
  WitnessProtectionDecision,
  WitnessProtectionStatus,
} from "types";
import {
  DateView,
  errorHandler,
  errorHandlerOnForm,
  hasRole,
} from "utils/helper";
import { BASE_URL } from "utils/constant";
import FinalDecision from "./modal/FinalDecision";
import Grievance from "./modal/Grievance";
import ProsecutorFeedback from "./modal/ProsecutorFeedback";
import ProtectionAssignProsecutor from "./modal/ProtectionAssignProsecutor";
import ProtectionType from "./modal/ProtectionType";
import WitnessForm from "./modal/WitnessForm";

const getDecisionChip = (decision?: string) => {
  const baseClasses =
    "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border";

  if (!decision)
    return `${baseClasses} bg-gray-100 text-gray-800 border-gray-300`;
  if (decision === "Rejected")
    return `${baseClasses} bg-red-50 text-red-800 border-red-200`;
  if (decision === "Approved" || decision === "Accepted")
    return `${baseClasses} bg-green-50 text-green-800 border-green-200`;
  if (decision === "Pending" || decision === "Under Review")
    return `${baseClasses} bg-amber-50 text-amber-800 border-amber-200`;
  return `${baseClasses} bg-gray-100 text-gray-800 border-gray-300`;
};

const WP_STAGE_STYLES: Record<string, string> = {
  green: "bg-green-100 text-green-800 border-green-200",
  red: "bg-red-100 text-red-800 border-red-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  indigo: "bg-indigo-100 text-indigo-800 border-indigo-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  purple: "bg-purple-100 text-purple-800 border-purple-200",
  gray: "bg-gray-100 text-gray-800 border-gray-300",
};

// Derive the real lifecycle stage of a witness protection request — mirrors
// the bail-right list badge. The raw `status` alone is ambiguous (it returns
// to "Forwarded to Coordinator" both before AND after the prosecutor weighs
// in), so we look at protection_type / prosecutor_feedback / decision_status.
const deriveWPStage = (
  p: any,
  t: any,
): { label: string; color: string; icon: string } => {
  const status = (p?.status || "").toString();
  const decision = (p?.decision_status || "").toString();

  // Final decision recorded by the deputy.
  if (decision === "Granted")
    return {
      label: t("witness_protection.stage.granted", {
        defaultValue: "Protection Granted",
      }),
      color: "green",
      icon: "✅",
    };
  if (decision === "Rejected")
    return {
      label: t("witness_protection.stage.rejected", {
        defaultValue: "Protection Rejected",
      }),
      color: "red",
      icon: "⛔",
    };

  switch (status) {
    case "Pending":
      return {
        label: t("witness_protection.stage.intake", {
          defaultValue: "Intake — Registering Witnesses",
        }),
        color: "amber",
        icon: "📝",
      };
    case "Forwarded to Coordinator":
      if (p?.prosecutor_feedback)
        return {
          label: t("witness_protection.stage.coordinator_review", {
            defaultValue: "Awaiting Coordinator Review",
          }),
          color: "amber",
          icon: "⏳",
        };
      if (!p?.protection_type)
        return {
          label: t("witness_protection.stage.with_coordinator", {
            defaultValue: "With Coordinator",
          }),
          color: "purple",
          icon: "📥",
        };
      return {
        label: t("witness_protection.stage.awaiting_assignment", {
          defaultValue: "Awaiting Prosecutor Assignment",
        }),
        color: "purple",
        icon: "👨‍⚖️",
      };
    case "Assigned to Prosecutor":
      return {
        label: t("witness_protection.stage.with_prosecutor", {
          defaultValue: "With Prosecutor",
        }),
        color: "indigo",
        icon: "👨‍⚖️",
      };
    case "Forwarded to Director":
      return {
        label: t("witness_protection.stage.with_director", {
          defaultValue: "With Director",
        }),
        color: "blue",
        icon: "📤",
      };
    case "Forwarded to Deputy":
      return {
        label: t("witness_protection.stage.awaiting_final_decision", {
          defaultValue: "Awaiting Final Decision",
        }),
        color: "amber",
        icon: "⚖️",
      };
    default:
      return {
        label: status || t("na"),
        color: "gray",
        icon: "",
      };
  }
};

const WPStageBadge: React.FC<{ protection: any; t: any }> = ({
  protection,
  t,
}) => {
  const { label, color, icon } = deriveWPStage(protection, t);
  return (
    <span
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${
        WP_STAGE_STYLES[color] || WP_STAGE_STYLES.gray
      }`}
    >
      {icon && <span>{icon}</span>}
      {label}
    </span>
  );
};

// Enhanced Chevron Component
const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
      open ? "rotate-180" : ""
    }`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

// Reusable Info Card Component
const InfoCard: React.FC<{
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badgeCount?: number;
  icon?: string;
  className?: string;
}> = ({
  title,
  children,
  defaultOpen = true,
  badgeCount,
  icon = "📋",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md ${className}`}
    >
      <button
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/70 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 shrink-0 rounded-lg bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center text-red-600 text-base">
            {icon}
          </div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {badgeCount !== undefined && (
            <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full font-semibold">
              {badgeCount}
            </span>
          )}
        </div>
        <Chevron open={isOpen} />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-1 border-t border-gray-100">{children}</div>
      )}
    </div>
  );
};

// Professional label/value tile used inside the grouped info grids.
const FieldItem: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50/60 px-3.5 py-2.5">
    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </dt>
    <dd className="text-sm font-semibold text-gray-900 mt-0.5 break-words">
      {value}
    </dd>
  </div>
);

// Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "danger" | "outline";
  className?: string;
}> = ({ onClick, children, variant = "primary", className = "" }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    danger: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const WitnessProtectionDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const [protection, setProtection] = useState<WitnessProtection>();
  const [loading, setLoading] = useState(true);
  const [openWitnessForm, setOpenWitnessForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openProtectionType, setOpenProtectionType] = useState(false);
  const [openAssignProsecutor, setOpenAssignProsecutor] = useState(false);
  const [openProsecutorFeedback, setOpenProsecutorFeedback] = useState(false);
  const [openDecision, setOpenDecision] = useState(false);
  const [openGrievance, setOpenGrievance] = useState(false);
  const [selectedWitness, setSelectedWitness] =
    useState<WitnessForWitnessProtection | null>(null);

  const [witnessOpen, setWitnessOpen] = useState<Record<number, boolean>>({});

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isDirector = hasRole(loginData?.roles, [Roles.prosecutionDirector]);
  const isDeputy = hasRole(loginData?.roles, [Roles.deputy]);

  const fetchData = () => {
    if (!id) return;
    setLoading(true);
    fetchWitnessProctionById(id)
      .then((data) => {
        setProtection(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_case_details"),
          "error",
        );
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (protection?.witnesses) {
      setWitnessOpen((prev) => {
        const next = { ...prev };
        (protection?.witnesses ?? []).forEach((_, idx) => {
          if (next[idx] === undefined) next[idx] = true;
        });
        return next;
      });
    }
  }, [protection?.witnesses?.length]);

  const updateStatus = (status: keyof typeof WitnessProtectionStatus) => {
    editWitnessProtaction2({
      id: id,
      status: status,
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  const updateDecision = (decision: keyof typeof WitnessProtectionDecision) => {
    editWitnessProtaction2({
      id: id,
      decision_status: decision,
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  const toggleWitness = (i: number) =>
    setWitnessOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  // Enhanced Header Component — bail-right styled gradient card.
  const renderHeader = () => (
    <div className="shrink-0 relative overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
      {/* Left accent bar */}
      <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-red-600 to-rose-500" />
      <div className="p-6 pl-7 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-600 to-rose-500 flex items-center justify-center shadow-sm">
            <span className="text-2xl">🛡️</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("witness_protection.detail.case_title")}
            </h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <WPStageBadge protection={protection} t={t} />
              {protection?.crime_type && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 capitalize">
                  {protection.crime_type}
                </span>
              )}
              {protection?.court_registration_number && (
                <span className="text-xs text-gray-500">
                  • {t("witness_protection.detail.court_registration_number")}:{" "}
                  {protection.court_registration_number}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {protection?.status === "Pending" && isRegistrar && (
            <ActionButton
              variant="outline"
              onClick={() => navigate(`/witness-protection/request?id=${id}`)}
            >
              <span>✏️</span>
              {t("witness_protection.detail.edit_registration", {
                defaultValue: "Edit Registration",
              })}
            </ActionButton>
          )}
          {protection?.status === "Pending" && isRegistrar && (
            <ActionButton onClick={() => setOpenWitnessForm(true)}>
              <span>👤</span>
              {t("witness_protection.detail.register_witness")}
            </ActionButton>
          )}
          {protection?.status === "Pending" && isRegistrar && (
            <ActionButton
              onClick={() => updateStatus("Forwarded to Coordinator")}
            >
              <span>📤</span>
              {t("witness_protection.detail.forward_to_coordinator")}
            </ActionButton>
          )}
          {renderActionButtons()}
        </div>
      </div>
    </div>
  );

  // Stage-specific action buttons, rendered inside the main header.
  const renderActionButtons = () => {
    if (!protection) return null;

    return (
      <>
        {protection?.status === "Forwarded to Coordinator" &&
            !protection?.protection_type &&
            isCoordinator && (
              <ActionButton onClick={() => setOpenProtectionType(true)}>
                <span>🔒</span>
                {t("witness_protection.detail.submit_protection_type")}
              </ActionButton>
            )}
          {protection?.status === "Forwarded to Coordinator" &&
            protection?.decision_status === "Pending" &&
            protection?.protection_type &&
            isCoordinator && (
              <>
                {!protection?.assign_prosecutor_name && (
                  <ActionButton onClick={() => setOpenAssignProsecutor(true)}>
                    <span>👨‍⚖️</span>
                    {t("witness_protection.detail.assign_prosecutor")}
                  </ActionButton>
                )}
                {/* Coordinator may optionally skip the prosecutor and forward
                    straight to the director at this stage. */}
                <ActionButton
                  variant="outline"
                  onClick={() => updateStatus("Forwarded to Director")}
                >
                  <span>📤</span>
                  {t("witness_protection.detail.forward_to_director")}
                </ActionButton>
              </>
            )}
          {protection?.status === "Assigned to Prosecutor" &&
            !protection?.prosecutor_feedback &&
            isProsecutor && (
              <ActionButton onClick={() => setOpenProsecutorFeedback(true)}>
                <span>💬</span>
                {t("witness_protection.detail.submit_feedback")}
              </ActionButton>
            )}
          {protection?.status === "Assigned to Prosecutor" &&
            protection?.prosecutor_feedback &&
            isProsecutor && (
              <>
                <ActionButton
                  variant="outline"
                  onClick={() => setOpenProsecutorFeedback(true)}
                >
                  <span>✏️</span>
                  {t("witness_protection.detail.edit_feedback")}
                </ActionButton>
                <ActionButton
                  onClick={() => updateStatus("Forwarded to Coordinator")}
                >
                  <span>📤</span>
                  {t("witness_protection.detail.forward_to_coordinator")}
                </ActionButton>
              </>
            )}
          {protection?.status === "Forwarded to Director" && isDirector && (
            <>
              {/* <ActionButton
                variant="danger"
                onClick={() => updateDecision("Rejected")}
              >
                <span>❌</span>
                Reject
              </ActionButton> */}
              <ActionButton onClick={() => updateStatus("Forwarded to Deputy")}>
                <span>📤</span>
                {t("witness_protection.detail.forward_to_deputy")}
              </ActionButton>
            </>
          )}
          {protection?.status === "Forwarded to Deputy" &&
            protection?.decision_status === "Pending" &&
            isDeputy && (
              <>
                <ActionButton
                  variant="danger"
                  onClick={() => updateDecision("Rejected")}
                >
                  <span>❌</span>
                  {t("witness_protection.detail.reject")}
                </ActionButton>
                <ActionButton onClick={() => setOpenDecision(true)}>
                  <span>✅</span>
                  {t("witness_protection.detail.grant_protection")}
                </ActionButton>
              </>
            )}
        {protection?.status === "Forwarded to Deputy" &&
          protection?.decision_status !== "Pending" &&
          isDeputy && (
            <ActionButton onClick={() => setOpenGrievance(true)}>
              <span>📝</span>
              {t("witness_protection.detail.grievance")}
            </ActionButton>
          )}
      </>
    );
  };

  // Enhanced Witness Card Component
  const WitnessCard: React.FC<{
    witness: WitnessForWitnessProtection;
    index: number;
  }> = ({ witness, index }) => {
    const isOpen = !!witnessOpen[index];

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <span className="text-purple-600">👤</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {`${t("witness")} #${index + 1}`}
              </h3>
              <p className="text-gray-600 text-sm">
                {witness.witness_full_name}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isRegistrar && protection?.status === "Pending" && (
              <>
                <button
                  onClick={() => {
                    setSelectedWitness(witness);
                    setOpenWitnessForm(true);
                  }}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={`${t("edit")} ${t("witness")}`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    setSelectedWitness(witness);
                    setOpenDelete(true);
                  }}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t("witness_protection.detail.delete_witness")}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </>
            )}
            <button
              onClick={() => toggleWitness(index)}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Chevron open={isOpen} />
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="px-6 pb-6 border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: t("full_name"), value: witness.witness_full_name },
                { label: t("age"), value: witness.witness_age },
                { label: t("gender"), value: witness.witness_gender },
                { label: t("region"), value: witness.witness_region },
                { label: t("zone"), value: witness.witness_zone },
                { label: t("woreda"), value: witness.witness_woreda },
                {
                  label: t("phone_number"),
                  value: witness.witness_phone_number,
                },
                {
                  label: t("date"),
                  value: DateView(witness.created_at),
                },
              ].map(
                (item, idx) =>
                  item.value && (
                    <FieldItem
                      key={idx}
                      label={item.label}
                      value={item.value}
                    />
                  ),
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:h-full flex flex-col bg-gray-50 py-6 relative">
      {(protection?.id || (protection as any)?.uu_id || id) && (
        <DirectorNotesButton
          model={FEEDBACK_MODEL.witnessProtection}
          objectId={
            (protection?.id || (protection as any)?.uu_id || id) as any
          }
        />
      )}
      <div className="px-4 sm:px-6 lg:px-8 flex flex-col flex-1 lg:min-h-0">
        {renderHeader()}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:flex-1 lg:min-h-0">
          {/* Left Column */}
          <div className="space-y-6 lg:overflow-y-auto lg:min-h-0 lg:pr-1">
            {/* Protection Information */}
            <InfoCard
              title={t(
                "witness_protection.detail.witness_protection_information",
              )}
              icon="🛡️"
              defaultOpen={true}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
                {[
                  {
                    label: t("witness_protection.detail.protection_type"),
                    value: protection?.protection_type,
                  },
                  {
                    label: t("witness_protection.detail.crime_type"),
                    value: protection?.crime_type,
                  },
                  {
                    label: t("witness_protection.detail.applicant_full_name"),
                    value: protection?.applicant_full_name,
                  },
                  {
                    label: t("witness_protection.detail.applicant_region"),
                    value: protection?.applicant_region,
                  },
                  {
                    label: t("witness_protection.detail.applicant_city"),
                    value: protection?.applicant_city,
                  },
                  {
                    label: t("witness_protection.detail.applicant_woreda"),
                    value: protection?.applicant_woreda,
                  },
                  {
                    label: t(
                      "witness_protection.detail.applicant_phone_number",
                    ),
                    value: protection?.applicant_phone_number,
                  },
                  {
                    label: t(
                      "witness_protection.detail.applicant_id_card_number",
                    ),
                    value: protection?.applicant_id_card_number,
                  },
                  {
                    label: t("witness_protection.detail.submission_method"),
                    value: protection?.submission_method,
                  },
                  {
                    label: t(
                      "witness_protection.detail.court_registration_number",
                    ),
                    value: protection?.court_registration_number,
                  },
                  {
                    label: t("witness_protection.detail.requested_date"),
                    value: DateView(protection?.created_at),
                  },
                ].map(
                  (item, idx) =>
                    item.value && (
                      <FieldItem
                        key={idx}
                        label={item.label}
                        value={item.value}
                      />
                    ),
                )}
              </div>
            </InfoCard>

            {/* Witnesses */}
            <div className="space-y-4">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center text-purple-600 text-base">
                    👥
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">
                    {t("witness_protection.detail.witnesses")}
                  </h3>
                </div>
                <span className="bg-purple-100 text-purple-800 text-xs px-3 py-1 rounded-full font-semibold">
                  {t("witness_protection.detail.witnesses_count", {
                    count: protection?.witnesses?.length || 0,
                  })}
                </span>
              </div>

              {protection?.witnesses?.length ? (
                <div className="space-y-4">
                  {protection.witnesses.map((witness, index) => (
                    <WitnessCard key={index} witness={witness} index={index} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 bg-white rounded-xl border border-gray-200">
                  <svg
                    className="w-12 h-12 text-gray-300 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <p className="text-gray-500">
                    {t("witness_protection.detail.no_witnesses_registered")}
                  </p>
                </div>
              )}
            </div>

            {/* Protection Request Reason & Remark */}
            {(protection?.protection_request_reason || protection?.remark) && (
              <InfoCard
                title={t(
                  "witness_protection.detail.protection_request_details",
                )}
                icon="📝"
              >
                <div className="space-y-4">
                  {protection?.protection_request_reason && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        {t(
                          "witness_protection.detail.protection_request_reason",
                        )}
                      </h4>
                      <p className="text-gray-700 leading-relaxed">
                        {protection.protection_request_reason}
                      </p>
                    </div>
                  )}
                  {protection?.remark && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        {t("witness_protection.detail.remark")}
                      </h4>
                      <p className="text-gray-700 leading-relaxed">
                        {protection.remark}
                      </p>
                    </div>
                  )}
                </div>
              </InfoCard>
            )}

            {/* Attachments */}

            <InfoCard
              title={t("witness_protection.detail.attachments")}
              icon="📎"
            >
              <div className="space-y-3">
                <DocumentViewerModal
                  documents={(protection?.documents || []) as any}
                  baseUrl={`${BASE_URL}${FILE_URL.WITNESS_PROTECTION}`}
                  layout="list"
                  // label="Letter Upload"
                  hideEmptyFile={true}
                />
                {/* {protection?.id_card_attachment_info?.file_url && (
                    <FileAttachment
                      name="ID Card"
                      url={protection.id_card_attachment_info.file_url}
                    />
                  )}
                  {protection?.petition_document_info?.file_url && (
                    <FileAttachment
                      name="Petition Document"
                      url={protection.petition_document_info.file_url}
                    />
                  )}
                  {protection?.supplementary_document_info?.file_url && (
                    <FileAttachment
                      name="Supplementary Document"
                      url={protection.supplementary_document_info.file_url}
                    />
                  )} */}
              </div>
            </InfoCard>
          </div>

          {/* Right Column */}
          <div className="space-y-6 lg:overflow-y-auto lg:min-h-0 lg:pr-1">
            {/* Inline Register / Edit Witness form */}
            {openWitnessForm && protection && (
              <WitnessForm
                inline
                protection={protection}
                witness={selectedWitness ?? undefined}
                onClose={(reload) => {
                  setOpenWitnessForm(false);
                  setSelectedWitness(null);
                  if (reload) fetchData();
                }}
              />
            )}

            {/* Inline Submit Protection Type form */}
            {openProtectionType && protection && (
              <ProtectionType
                inline
                protection={protection}
                onClose={(reload) => {
                  setOpenProtectionType(false);
                  if (reload) fetchData();
                }}
              />
            )}

            {/* Inline Assign Prosecutor form */}
            {openAssignProsecutor && protection && (
              <ProtectionAssignProsecutor
                inline
                protection={protection}
                onClose={(reload) => {
                  setOpenAssignProsecutor(false);
                  if (reload) fetchData();
                }}
              />
            )}

            {/* Decision Section */}
            {protection?.decision_summary && (
              <div
                className={`rounded-xl shadow-sm p-6 ${
                  protection.decision_status === "Granted"
                    ? "bg-green-50 border border-green-200"
                    : "bg-white border border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-green-600">⚖️</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {t("witness_protection.detail.decision_status")}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`${getDecisionChip(
                          protection?.decision_status,
                        )}`}
                      >
                        {protection?.decision_status}
                      </span>
                      {protection.date_signed && (
                        <span className="text-sm text-gray-600">
                          • {DateView(protection.date_signed)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      {t("witness_protection.detail.decision_summary")}
                    </label>
                    <p className="text-gray-900 mt-1">
                      {protection.decision_summary}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t("witness_protection.detail.agreement_time_period")}
                      </label>
                      <p className="text-gray-900 mt-1">
                        {protection.protection_agreement_time_period || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t("witness_protection.detail.protection_duration")}
                      </label>
                      <p className="text-gray-900 mt-1">
                        {DateView(
                          protection.protection_agreement_time_period_begin_date,
                        ) || "-"}
                        {" — "}
                        {DateView(
                          protection.protection_agreement_time_period_end_date,
                        ) || "-"}
                      </p>
                    </div>
                  </div>
                  {protection.new_decision_summary && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t(
                          "witness_protection.detail.updated_protection_decision",
                        )}
                      </label>
                      <p className="text-gray-900 mt-1">
                        {protection.new_decision_summary}
                      </p>
                    </div>
                  )}

                  {/* Decision Attachments */}
                  <div className="pt-4 space-y-3">
                    <DocumentViewerModal
                      documents={
                        (protection?.documents?.filter(
                          (a) => a?.document_type === "decision_file",
                        ) || []) as any
                      }
                      baseUrl={`${BASE_URL}${FILE_URL.WITNESS_PROTECTION}`}
                      layout="list"
                      label={t("witness_protection.detail.decision_file")}
                      hideEmptyFile={true}
                    />
                    <DocumentViewerModal
                      documents={
                        (protection?.documents?.filter(
                          (a) => a?.document_type === "agreement_document",
                        ) || []) as any
                      }
                      baseUrl={`${BASE_URL}${FILE_URL.WITNESS_PROTECTION}`}
                      layout="list"
                      label={t("witness_protection.detail.agreement_document")}
                      hideEmptyFile={true}
                    />
                    {/* {protection?.decision_file_info?.file_url && (
                      <FileAttachment
                        name="Decision File"
                        url={protection.decision_file_info.file_url}
                      />
                    )} */}
                    {/* {protection?.agreement_document_info?.file_url && (
                      <FileAttachment
                        name="Agreement Document"
                        url={protection.agreement_document_info.file_url}
                      />
                    )} */}
                  </div>
                </div>
              </div>
            )}

            {/* Prosecutor Section */}
            {(protection?.assign_prosecutor_name ||
              protection?.prosecutor_feedback) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <span className="text-red-600">👨‍⚖️</span>
                  </div>
                  <h3 className="font-semibold text-gray-900">
                    {t("witness_protection.detail.assigned_prosecutor")}
                  </h3>
                </div>

                <div className="space-y-3">
                  {protection?.assign_prosecutor_name && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t("witness_protection.detail.prosecutor_name")}
                      </label>
                      <p className="text-gray-900 mt-1">
                        {protection.assign_prosecutor_name}
                      </p>
                    </div>
                  )}
                  {protection?.prosecutor_feedback && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t("witness_protection.detail.prosecutor_feedback")}
                      </label>
                      <p className="text-gray-900 mt-1">
                        {protection.prosecutor_feedback}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Security & Monitoring */}
            {(protection?.security_organization_involved ||
              protection?.monitoring_details) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <span className="text-purple-600">🔒</span>
                  </div>
                  <h3 className="font-semibold text-gray-900">
                    {t("witness_protection.detail.security_and_monitoring")}
                  </h3>
                </div>

                <div className="space-y-3">
                  {protection?.security_organization_involved && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t("witness_protection.detail.security_organization")}
                      </label>
                      <p className="text-gray-900 mt-1">
                        {protection.security_organization_involved}
                      </p>
                    </div>
                  )}
                  {protection?.monitoring_details && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t("witness_protection.detail.monitoring_details")}
                      </label>
                      <p className="text-gray-900 mt-1">
                        {protection.monitoring_details}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Grievance */}
            {protection?.grievance_submitted && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <span className="text-red-600">⚠️</span>
                  </div>
                  <h3 className="font-semibold text-gray-900">
                    {t(
                      "witness_protection.detail.grievance_on_document_decision",
                    )}
                  </h3>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">
                    {t("witness_protection.detail.grievance_description")}
                  </label>
                  <p className="text-gray-900 mt-1">
                    {protection.grievance_description || "-"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        {openProsecutorFeedback && protection && (
          <ProsecutorFeedback
            protection={protection}
            onClose={(reload) => {
              setOpenProsecutorFeedback(false);
              if (reload) fetchData();
            }}
          />
        )}

        {openDecision && protection && (
          <FinalDecision
            protection={protection}
            onClose={(reload) => {
              setOpenDecision(false);
              if (reload) fetchData();
            }}
          />
        )}

        {openGrievance && protection && (
          <Grievance
            protection={protection}
            onClose={(reload) => {
              setOpenGrievance(false);
              if (reload) fetchData();
            }}
          />
        )}

        {selectedWitness && (
          <DeleteModal
            isOpen={openDelete}
            onConfirm={() => {
              deleteWitnessToWitnessProction(selectedWitness)
                .then(() => {
                  showTooltip(t("successfully_deleted"), "success");
                  fetchData();
                })
                .catch((error) => {
                  showTooltip(
                    error?.response?.data?.detail || errorHandler(error),
                  );
                });
            }}
            onCancel={() => {
              setOpenDelete(false);
              setSelectedWitness(null);
            }}
            title={t("witness_protection.detail.delete_witness")}
            message={t("witness_protection.detail.delete_witness_confirm", {
              witnessName: selectedWitness?.witness_full_name,
            })}
          />
        )}
      </div>
    </div>
  );
};

export default WitnessProtectionDetail;
